import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';
import { z } from 'zod';

import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolExecutionContext,
} from '../../types';
import { Tool } from '../Tool';

import { Urls } from 'js/src/client';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';

export const suggestUpgradeSchema = z.object({
    feature: z
        .enum([
            'video_duration',
            'custom_fonts',
            'remove_branding',
            'custom_watermark',
            'premium_ai_voices',
            'generate_ai_video_clip',
            'ai_avatar',
        ])
        .describe('The premium feature the user is trying to use.'),
    reason: z
        .string()
        .describe(
            'A brief, user-friendly explanation of why they need to upgrade. Keep it simple and factual based on current plan limits (e.g., "Videos longer than 2 minutes require a paid plan"). Do NOT mention specific plan names or suggest which plan to upgrade to.',
        ),
});
export type SuggestUpgradeArgs = z.infer<typeof suggestUpgradeSchema>;

/**
 * Custom renderer component for the upgrade tool that displays an upgrade button
 */
const UpgradeToolRenderer: React.FC<CustomRendererComponentProps> = ({ toolCall }) => {
    const args = toolCall?.function?.arguments
        ? (JSON.parse(toolCall.function.arguments) as SuggestUpgradeArgs)
        : { feature: 'unknown', reason: 'Upgrade to unlock premium features' };

    const { feature, reason } = args;

    const handleUpgradeClick = React.useCallback(() => {
        trackUpsellClicked(`sketch_upgrade_${feature}`);
        analytics.track('Sketch: Upgrade Tool Clicked', {
            feature,
            reason,
        });
        window.open(Urls.PRICING, '_blank');
    }, [feature, reason]);

    return (
        <div className="my-l5-8">
            <Callout
                msgType={CALLOUT_TYPES.UPSELL}
                text={reason}
                ctaText="View plans"
                onCtaClick={handleUpgradeClick}
            />
        </div>
    );
};

/**
 * Tool for suggesting users upgrade their plan when they request premium features.
 * This tool displays a custom upgrade button that opens the pricing page.
 */
export class SuggestUpgradeTool extends Tool<typeof suggestUpgradeSchema> {
    protected override readonly schema = suggestUpgradeSchema;
    protected override readonly toolName = TOOL_NAMES.suggestUpgrade;
    protected override readonly toolDescription =
        'Display an upgrade button to the user when they ask to do something that requires a premium feature that is not available on their current plan. Use this to help users discover upgrade options.';

    public override customRenderer = UpgradeToolRenderer;
    public override customRendererMode = CustomRendererMode.FULL;

    public override getToolDisplayName(): string {
        return 'Suggesting upgrade';
    }

    public get statusIcon(): string {
        return 'upgrade';
    }

    async execute(
        args: SuggestUpgradeArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        // The actual rendering is handled by the custom renderer
        // Tell the model not to add additional text since the UI already shows the upgrade prompt
        return {
            result: `Upgrade prompt displayed to user for feature: ${args.feature}. The user can see the upgrade button and your message "${args.reason}" - do not repeat this information or add further explanation.`,
            isSuccess: true,
        };
    }
}
